import { Field } from '@catering/ui';
import { type JSX, useEffect, useRef, useState } from 'react';

import {
  MENU_STATUS_FILTERS,
  MENU_STATUS_FILTER_LABELS,
  type Allergen,
  type DietaryTag,
  type MenuStatusFilter,
  type ServiceType,
} from '../../../menus/api';

const SEARCH_DEBOUNCE_MS = 300;

export interface MenuSearchBarProps {
  query: string;
  serviceTypeId: string | undefined;
  dietaryTagId: string | undefined;
  excludeAllergenId: string | undefined;
  status: MenuStatusFilter | undefined;
  serviceTypes: ServiceType[];
  dietaryTags: DietaryTag[];
  allergens: Allergen[];
  onQueryChange: (query: string) => void;
  onServiceTypeChange: (serviceTypeId: string | undefined) => void;
  onDietaryTagChange: (dietaryTagId: string | undefined) => void;
  onExcludeAllergenChange: (allergenId: string | undefined) => void;
  onStatusChange: (status: MenuStatusFilter | undefined) => void;
}

/**
 * The text field debounces locally; the rest are `<select>`s that commit
 * immediately, same convention as `CustomerSearchBar` (`design.md` §7: no
 * shared `Select` component yet).
 */
export function MenuSearchBar({
  query,
  serviceTypeId,
  dietaryTagId,
  excludeAllergenId,
  status,
  serviceTypes,
  dietaryTags,
  allergens,
  onQueryChange,
  onServiceTypeChange,
  onDietaryTagChange,
  onExcludeAllergenChange,
  onStatusChange,
}: MenuSearchBarProps): JSX.Element {
  const [draft, setDraft] = useState(query);
  const [syncedQuery, setSyncedQuery] = useState(query);

  // Keeps the draft aligned when the committed query changes from outside
  // (e.g. browser back/forward) without fighting the user's own typing.
  if (query !== syncedQuery) {
    setSyncedQuery(query);
    setDraft(query);
  }

  const onQueryChangeRef = useRef(onQueryChange);
  useEffect(() => {
    onQueryChangeRef.current = onQueryChange;
  });

  useEffect(() => {
    const trimmed = draft.trim();

    if (trimmed === query) {
      return;
    }

    const timeout = setTimeout(() => onQueryChangeRef.current(trimmed), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [draft, query]);

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="min-w-64 flex-1">
        <Field
          label="Buscar"
          name="q"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Nombre o código del menú"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="menu-service-type-filter" className="text-sm font-medium text-slate-900">
          Tipo de servicio
        </label>
        <select
          id="menu-service-type-filter"
          value={serviceTypeId ?? ''}
          onChange={(event) =>
            onServiceTypeChange(event.target.value === '' ? undefined : event.target.value)
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todos los tipos</option>
          {serviceTypes.map((serviceType) => (
            <option key={serviceType.id} value={serviceType.id}>
              {serviceType.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="menu-dietary-tag-filter" className="text-sm font-medium text-slate-900">
          Etiqueta dietaria
        </label>
        <select
          id="menu-dietary-tag-filter"
          value={dietaryTagId ?? ''}
          onChange={(event) =>
            onDietaryTagChange(event.target.value === '' ? undefined : event.target.value)
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todas las etiquetas</option>
          {dietaryTags.map((dietaryTag) => (
            <option key={dietaryTag.id} value={dietaryTag.id}>
              {dietaryTag.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="menu-exclude-allergen-filter"
          className="text-sm font-medium text-slate-900"
        >
          Libre de alérgeno
        </label>
        <select
          id="menu-exclude-allergen-filter"
          value={excludeAllergenId ?? ''}
          onChange={(event) =>
            onExcludeAllergenChange(event.target.value === '' ? undefined : event.target.value)
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Sin filtrar</option>
          {allergens.map((allergen) => (
            <option key={allergen.id} value={allergen.id}>
              {allergen.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="menu-status-filter" className="text-sm font-medium text-slate-900">
          Estado
        </label>
        <select
          id="menu-status-filter"
          value={status ?? ''}
          onChange={(event) =>
            onStatusChange(
              event.target.value === '' ? undefined : (event.target.value as MenuStatusFilter),
            )
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todos</option>
          {MENU_STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {MENU_STATUS_FILTER_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
